import { type Inspection } from '@lib/spproject/types';
import { handleApiResult } from '@store/error';
import type { InspectionAuthor } from './inspectionEndpoints';
import { apiSlice } from './apiSlice';

/** An inspection as the recent list draws it: what it is, where it lives, who filed it. */
export interface RecentInspection {
  inspectionId: string;
  projectId: string;
  /** Empty when the project has since been deleted; the row still links to it. */
  projectName: string;
  name: string;
  dateModified: string;
  /** Absent or null where the upload stored none. */
  status?: Inspection.NoticeType | null;
  author?: InspectionAuthor | null;
}

export interface IStatistic {
  projectCount: number;
  checklistCount: number;
  inspectionCount: number;
  dailyInspections: { dateModified: string; total: number; status?: { [K in Inspection.NoticeType]?: number; } }[];
  recentInspections: RecentInspection[];
}

/** The paged form of the same rows: `total` is what the pager counts, not `data.length`. */
export interface RecentInspectionPage {
  data: RecentInspection[];
  meta: { page: number; limit: number; total: number; pages: number };
}

export const statisticApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getStatistics: builder.query<IStatistic, void>({
      query: () => '/v1/statistics',
      providesTags: ['Statistic'],
      onQueryStarted: handleApiResult(),
    }),
    /**
     * A page of the recent list. Each page is its own cache entry, so walking back to one
     * already seen costs no request and walking forward costs exactly one.
     */
    getRecentInspections: builder.query<RecentInspectionPage, { page: number; limit: number }>({
      query: ({ page, limit }) => `/v1/statistics/inspections?page=${page}&limit=${limit}`,
      providesTags: ['Statistic'],
      onQueryStarted: handleApiResult(),
    }),
  }),
});

export const {
  useGetStatisticsQuery,
  useLazyGetStatisticsQuery,
  useGetRecentInspectionsQuery,
} = statisticApiSlice;
